import { zodResolver } from '@hookform/resolvers/zod';
import { KeyboardEvent, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Checkbox, CheckboxGroup, Loader, Modal } from 'rsuite';
import { ButtonPrimary, SearchInput } from '../../../../../../../components';
import { SELECT_USERS_PAGE_SIZE } from '../../constants';
import { useContractUsersQuery, usePagination } from '../../hooks';
import { SelectUsersFormValues, selectUsersFormSchema } from '../../schema';
import { PermittedUser } from '../../types';
import { filterUsers, formatSelectedUsersCount, mergeWithSelectedUsers } from '../../utils';
import { UsersPagination } from '../UsersPagination';
import styles from './SelectUsersModal.module.scss';

type SelectUsersModalProps = {
  selectedUsers: PermittedUser[];
  onClose: () => void;
  onSave: (users: PermittedUser[]) => void;
};

// Enter na busca não deve enviar o formulário do modal.
const preventEnterSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') event.preventDefault();
};

const toUserIds = (values: unknown[]): number[] => values.filter((value): value is number => typeof value === 'number');

export const SelectUsersModal = ({ selectedUsers, onClose, onSave }: SelectUsersModalProps) => {
  const contractUsersQuery = useContractUsersQuery(true);

  const form = useForm<SelectUsersFormValues>({
    resolver: zodResolver(selectUsersFormSchema),
    defaultValues: {
      search: '',
      selectedIds: selectedUsers.map(user => user.cd_usuario),
    },
  });

  const search = form.watch('search');
  const selectedIds = form.watch('selectedIds');

  const users = useMemo(() => mergeWithSelectedUsers(contractUsersQuery.data ?? [], selectedUsers), [contractUsersQuery.data, selectedUsers]);
  const visibleUsers = useMemo(() => filterUsers(users, search), [users, search]);
  const { page, pageItems: pageUsers, onChangePage } = usePagination(visibleUsers, SELECT_USERS_PAGE_SIZE);

  const submit = form.handleSubmit(values => {
    onSave(users.filter(user => values.selectedIds.includes(user.cd_usuario)));
  });

  const renderList = () => {
    if (contractUsersQuery.isError) return <div className={styles.feedback}>Não foi possível carregar os usuários.</div>;
    if (contractUsersQuery.isLoading) return <Loader center content="Carregando usuários..." />;
    if (visibleUsers.length === 0) return <div className={styles.feedback}>Nenhum usuário encontrado. Verifique o nome ou e-mail digitado.</div>;

    return (
      <Controller
        control={form.control}
        name="selectedIds"
        render={({ field }) => (
          <CheckboxGroup className={styles.group} name={field.name} value={field.value} onChange={(values: unknown[]) => field.onChange(toUserIds(values))}>
            {pageUsers.map(user => (
              <Checkbox key={user.cd_usuario} className={styles.option} value={user.cd_usuario}>
                <span className={styles.name}>{user.user_name}</span>
                <span className={styles.email}>{user.user_email}</span>
              </Checkbox>
            ))}
          </CheckboxGroup>
        )}
      />
    );
  };

  return (
    <Modal className={styles.modal} show onHide={onClose} size="sm" backdrop="static" keyboard={false}>
      <form onSubmit={submit}>
        <Modal.Header>
          <Modal.Title>Selecionar usuários</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Escolha os usuários que poderão aplicar preços no Gerenciador de Preços. Você pode selecionar mais de um.</p>
          <Controller
            control={form.control}
            name="search"
            render={({ field }) => (
              <SearchInput
                aria-label="Buscar por nome ou e-mail"
                placeholder="Buscar por nome ou e-mail..."
                value={field.value}
                onChange={event => {
                  field.onChange(event);
                  onChangePage(1);
                }}
                onKeyDown={preventEnterSubmit}
              />
            )}
          />
          <div className={styles.listbox}>{renderList()}</div>
          <div className={styles.listFooter}>
            <p className={styles.counter}>{formatSelectedUsersCount(selectedIds.length)}</p>
            <UsersPagination total={visibleUsers.length} pageSize={SELECT_USERS_PAGE_SIZE} page={page} onChangePage={onChangePage} />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <ButtonPrimary size="medium" theme="ghost" type="button" onClick={onClose}>
            cancelar
          </ButtonPrimary>
          <ButtonPrimary size="medium" type="submit">
            salvar
          </ButtonPrimary>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
